import { KeyboardArrowDownOutlined, KeyboardArrowUpOutlined } from '@mui/icons-material';
import type React from 'react';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';

import type { VNFieldHelpProps } from '@/components/VNFieldHelp';
import {
  hasClickableFieldHelp,
  hasFieldHelpIcon,
  VNFieldHelpChrome,
  VNFieldHelpHeading,
  VNFieldHelpIconButton,
  useVNFieldHelpController,
} from '@/components/VNFieldHelp';
import VNSwitch from '@/components/VNSwitch/VNSwitch';

interface VNSectionProps extends VNFieldHelpProps {
  name: string;
  label: string;
  variant?: 'switch' | 'dropdown';
  dropdownTitle?: React.ReactNode;
  isOpen?: boolean;
  toggleCallback?: () => void;
  children: React.ReactNode;
}

type SwitchHeaderProps = {
  name: string;
  label: string;
} & VNFieldHelpProps;

const VNSectionSwitchHeader = ({ name, label, ...fieldHelpProps }: SwitchHeaderProps) => {
  const showIcon = hasFieldHelpIcon(fieldHelpProps);
  const {
    containerRef,
    panelOpen,
    drawerOpen,
    isActive,
    clickable,
    handleIconClick,
    drawerAriaLabel,
    closeDrawer,
    helperTooltip,
    helperText,
    helperContent,
    helperLink,
    helperContentTitle,
  } = useVNFieldHelpController(fieldHelpProps);

  const headerClass = [
    'section-switch-header',
    showIcon && 'vn-field-help vn-field-help--heading',
    showIcon && panelOpen && 'vn-field-help--help-open',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={headerClass} ref={showIcon ? containerRef : undefined}>
      <div className="section-switch-row">
        <span className="section-switch-label">
          <span className="section-switch-label__text">{label}</span>
          {showIcon && (
            <VNFieldHelpIconButton
              active={isActive}
              helperTooltip={helperTooltip}
              onClick={hasClickableFieldHelp(fieldHelpProps) ? handleIconClick : undefined}
              ariaLabel={drawerAriaLabel}
            />
          )}
        </span>
        <VNSwitch name={name} label="" />
      </div>
      {showIcon && (
        <VNFieldHelpChrome
          panelOpen={panelOpen}
          drawerOpen={drawerOpen}
          helperText={helperText}
          helperLink={helperLink}
          helperContent={helperContent}
          helperContentTitle={helperContentTitle}
          onCloseDrawer={closeDrawer}
        />
      )}
    </div>
  );
};

function VNSection({
  name,
  label,
  variant = 'switch',
  dropdownTitle,
  isOpen,
  toggleCallback,
  children,
  ...fieldHelpProps
}: VNSectionProps) {
  const { watch } = useFormContext();
  const [open, setOpen] = useState(isOpen);
  const [prevIsOpen, setPrevIsOpen] = useState(isOpen);

  if (prevIsOpen !== isOpen) {
    setPrevIsOpen(isOpen);
    setOpen(isOpen);
  }

  const isExpanded = watch(name);

  if (variant === 'dropdown') {
    return (
      <div className="vn-section">
        <button
          type="button"
          className="section-header"
          onClick={() => {
            if (toggleCallback) {
              toggleCallback();
            }
            setOpen((prev) => !prev);
          }}
        >
          <div className="section-title">
            {dropdownTitle ?? <VNFieldHelpHeading className="title-text" title={label} {...fieldHelpProps} />}
            {open ? <KeyboardArrowUpOutlined className="icon" /> : <KeyboardArrowDownOutlined className="icon" />}
          </div>
        </button>
        <div className="container">{open && children}</div>
      </div>
    );
  }

  return (
    <div className="vn-section vn-section--switch">
      <VNSectionSwitchHeader name={name} label={label} {...fieldHelpProps} />
      <div className="container">{isExpanded && children}</div>
    </div>
  );
}

export default VNSection;
